<template>
    <div class="w-3/4 h-[calc(100vh-8rem)] fixed">
        <div class="place-autocomplete-card bg-white px-4 py-2 h-fit flex flex-col w-fit shadow-md max-w-[500px] lg:max-w-[850px] 2xl:max-width-[1200px] space-y-2 items-start justify-between rounded-sm mt-2.5" id="place-autocomplete-card">
            <div class="px-1 grid grid-cols-2 lg:flex items-end space-x-2 h-full">
                <div
                    class="rounded-sm hover:bg-gray-300 p-3 hover:cursor-pointer"
                    @click="toggleFilters"
                    ref="filterBoundary"
                >
                    <FilterSVG />
                </div>
                <div class="flex flex-col place-autocomplete-container w-[200px] lg:w-[300px] justify-between" id="place-autocomplete-container">
                    <div class="text-gray-800 tracking-wide text-sm font-medium whitespace-nowrap pb-1.5" :style="addressLabelStyle">
                        Search Address: 
                    </div>
                </div>
                <div class="flex flex-col justify-between">
                    <div class="text-gray-800 tracking-wide text-sm font-medium whitespace-nowrap pb-1.5">
                        Latitude:
                    </div>
                    <input type="number" id="latitude" min="-90" max="90" step="0.0000000000000001" v-model="pointOfInterestFilters.latitude" class="border p-2.5 w-full h-full text-sm rounded border-black" />
                </div>
                <div class="flex flex-col justify-between">
                    <div class="text-gray-800 tracking-wide text-sm font-medium whitespace-nowrap pb-1.5">
                        Longitude:
                    </div>
                    <input type="number" id="longitude" min="-180" max="180" step="0.0000000000000001" v-model="pointOfInterestFilters.longitude" class="border p-2.5 w-full h-full text-sm rounded border-black" />
                </div>
                <div class="flex flex-col justify-between h-full">
                    <div class="text-gray-800 tracking-wide text-sm font-medium whitespace-nowrap pb-1.5">
                        Search Radius:
                    </div>
                    <select class="border p-2 w-full h-full text-sm rounded border-black" v-model="pointOfInterestFilters.searchRadius" name="" id="">
                        <option v-for="searchRadiusOption in searchRadiusOptions" :key="searchRadiusOption.value" :value="searchRadiusOption.value">
                            {{ searchRadiusOption.label }}
                        </option>
                    </select>
                </div>
                <div class="h-full w-fit">
                    <button class="text-sm bg-dark-secondary hover:bg-dark-primary hover:text-gray-100 hover:shadow active:bg-dark-primary active:text-gray-400 text-white whitespace-nowrap tracking-wider font-medium px-4 py-2 rounded" @click="searchCompanyLocations">Search</button>
                </div>
            </div>
            <div v-show="visibleFilters" class="w-full max-w-[500px] lg:max-w-[850px] 2xl:max-width-[1200px] pb-2 border-t border-gray-300">
                <FilterForm
                    :filters="filters"
                />
            </div>
        </div>
        <div id="map" class="w-full h-full"></div>
    </div>
</template>

<script setup>
import { ref, reactive, defineProps, defineEmits, onMounted, watch, computed } from 'vue';
import FilterForm from '../base-components/FilterForm.vue';
import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from "@googlemaps/markerclusterer"; 
import FilterSVG from '../svg-components/FilterSVG.vue';


const props = defineProps(['reloadMap', 'mapOptions', 'companyLocations',  'boundaries', 'filters', 'selectedCompanyLocation', 'newCompanyRowSelected', 'newClusterRowSelected'])

const emit = defineEmits(['map-init-complete', 'fetch-company-locations', 'change-poi-search', 'set-clusters', 'select-location-pin', 'closed-info-window'])

const mapLoader = reactive(new Loader(
    {
        apiKey: 'AIzaSyA2xvlXjcnOddpRtuv4LG8v7mSgVYAWrS0',
        libraries: ['maps', 'marker', 'places'],
        region: 'US'
    })
)

const visibleFilters = ref(false)

// const map = ref(null)
const googleObject = ref(null)
const mapObject = ref(null)
const infoWindowObject = ref(null)
const markers = ref([])
const markerClusterer = ref(null)
const clusters = ref(null)

const newPinZoom = computed(() => {
    if(props.companyLocations && props.companyLocations.length > 0) {
        let numLocations = props.companyLocations.length
        if(numLocations >= 20000) {
            return 6
        }
        else if(numLocations >= 15000) {
            return 7
        }
        else if(numLocations >= 10000) {
            return 8
        }
        else if(numLocations >= 5000) {
            return 12
        } else {
            return 15
        }
    }
    return 15
})

const clusterRenderer = reactive ({
    render: function ({ count, position }, stats) {
        const color = getMarkerClusterColor(count, stats.clusters.markers.max);
        // create svg url with fill color
        // const parser = new DOMParser()
        // const svgString = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"><circle cx="200" cy="200" opacity="1" r="${(count / stats.clusters.markers.max) * 90 + 80}" /></svg>`
        // const clusterSvg = parser.parseFromString(svgString, "image/svg+xml").documentElement;
        // const svg = window.btoa(`
        //     <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400">
        //     <circle cx="200" cy="200" opacity="1" r="${(count / stats.clusters.markers.max) * 90 + 80}" />    
        //     </svg>`
        // );
        // create marker using svg icon
        // return new googleObject.value.maps.Marker({
        //     position,
        //     icon: {
        //         url: `data:image/svg+xml;base64,${svg}`,
        //         scaledSize: new googleObject.value.maps.Size(75, 75),
        //     },
        //     label: {
        //         text: String(count),
        //         color: "rgba(255,255,255,0.9)",
        //         fontSize: "12px",
        //     },
        //     // adjust zIndex to be above other markers
        //     zIndex: Number(googleObject.value.maps.Marker.MAX_ZINDEX) + count,
        // });


        // const clusterPin = new googleObject.value.maps.marker.PinElement({
        //     background: color,
        //     borderColor: color,
        //     glyph: `${String(count)}`,
        //     glyphColor: "white",
        //     scale: 1.5,
        // });

        const parser = new DOMParser();
        // const svg = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" width="90" height="90">
        //     <circle cx="200" cy="200" opacity="1" r="${(count / stats.clusters.markers.max) * 90 + 80}" />
        //     <circle cx="200" cy="200" opacity=".5" r="${(count / stats.clusters.markers.max) * 90 + 100}" />
        //     <circle cx="200" cy="200" opacity=".2" r="${(count / stats.clusters.markers.max) * 90 + 120}" />
        //     <text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
        //     </svg>`;
        const svg = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" width="90" height="90">
            <circle cx="200" cy="200" opacity="1" r="${80}" />
            <circle cx="200" cy="200" opacity=".5" r="${100}" />
            <circle cx="200" cy="200" opacity=".2" r="${120}" />
            <text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
            </svg>`;
        const svgElement = parser.parseFromString(svg, "image/svg+xml").documentElement;
        svgElement.setAttribute("transform", "translate(0 25)");

        return new googleObject.value.maps.marker.AdvancedMarkerElement({
            position,
            zIndex: Number(googleObject.value.maps.Marker.MAX_ZINDEX) + count,
            content: svgElement,

        });
    }
})

const pointOfInterestFilters = reactive({
    latitude: null,
    longitude: null,
    searchRadius: 1
})

const numLocations = ref(0)

const mapIsLoading = ref(false)

const placeAutocomplete = ref(null)

const addressLabelStyle = ref(null)

onMounted(() => {
    mapIsLoading.value = true
    // initMap(props.mapOptions)
    initMap(props.mapOptions)
    // loadMap()
    mapIsLoading.value = false
})

const searchRadiusOptions = ref([
    {value: 1, label: 'Small (~50 Miles)'},
    {value: 2, label: 'Medium (~100 Miles)'},
    {value: 4, label: 'Large (~200 Miles)'},
    {value: 6, label: 'Extra Large (~300 Miles)'},
])

// const defaultMapOptions = ref(
//     {
//         zoom: 5,
//         maxZoom: 17,
//         minZoom: 3,
//         center: { lat: 40.6331, lng: -89.3985 },
//         mapId: "99bc138cb59717d1",
//     }
// )

watch(() => props.reloadMap, () => {
    console.log('watch props.reloadMap')
    reloadMap()
    // initMap()
}, {deep:true})

watch(() => props.newCompanyRowSelected, () => {
    console.log('watch props.newCompanyRowSelected')
    centerMapAndZoom()
    openCompanyLocationInfoWindow()
})

watch(() => props.newClusterRowSelected, () => {
    console.log('watch props.newClusterRowSelected')
    centerMapAndZoom()
})

watch(() => props.mapOptions, () => {
    console.log('mapOptions changed')
    if(mapObject.value) {
        mapObject.value.setOptions(props.mapOptions)
    }
}, {deep:true})


watch(()=> pointOfInterestFilters, () => {
    emit('change-poi-search', pointOfInterestFilters)
}, {deep: true})

async function initMap(mapOptions) {
    console.log('initMap')
    mapLoader.load().then((google) => {
        const map = new google.maps.Map(document.getElementById("map"), mapOptions);
        mapObject.value = map
        googleObject.value = google

        // setMarkers()
        // === try to replace with setMarkers
        markers.value = props.companyLocations.map((companyLocation) => {
            // console.log(companyLocation.location.lat, companyLocation.location.long)
            const companyPin = new google.maps.marker.PinElement({
                background: "#0F102E",
                borderColor: "#0F102E",
                glyphColor: "white",
                scale: 0.75,
            });
            const marker = new google.maps.marker.AdvancedMarkerElement({
                position: {lat: parseFloat(companyLocation.location.lat), lng: parseFloat(companyLocation.location.long)},
                content: companyPin.element,

            });
            let city = companyLocation && companyLocation.location && companyLocation.location.city ? `${companyLocation.location.city}` : ''
            let state = companyLocation && companyLocation.location && companyLocation.location.state ? `, ${companyLocation.location.state}` : ''
            let country = companyLocation && companyLocation.location && companyLocation.location.country && companyLocation.location.country != 'USA' ? ` (${companyLocation.location.country})` : ''
            marker.setAttribute("city-state-country", `${city}${state}${country}`)
            marker.setAttribute("id", `marker-${companyLocation.id}`)
            marker.addListener('gmp-click', (event) => {
                handleLocationClick(marker, companyLocation);
            })
            // marker.content.getRootNode().addEventListener('click', (event) => {
            //     console.log('content click')
            //     handleLocationClick(marker, companyLocation);
            // })
            // marker.content.getRootNode().addEventListener('mouseenter', (event) => {
            //     console.log('mouseenter')
            //     // emit('hover-on-location', companyLocation.id)
            //     const hoverPin = new googleObject.value.maps.marker.PinElement({
            //         background: "#2E9CCA",
            //         borderColor: "#0F102E",
            //         glyphColor: "#0F102E",
            //         scale: 0.75,
            //     });
            //     marker.content = hoverPin.element
            // })
            // marker.content.getRootNode().addEventListener('mouseout', (event) => {
            //     const companyPin = new googleObject.value.maps.marker.PinElement({
            //         background: "#0F102E",
            //         borderColor: "#0F102E",
            //         glyphColor: "white",
            //         scale: 0.75,
            //     });
            //     console.log('mouseout')
            //     // emit('hover-out')
            //     marker.content = companyPin.element
            // })
            return marker;
        });
        numLocations.value = markers.value.length
        markerClusterer.value = new MarkerClusterer({ map: mapObject.value, markers: markers.value, renderer: clusterRenderer });
        clusters.value = markerClusterer.value.clusters.sort((a, b) => b.markers.length - a.markers.length)
        // ==== 

        initAutoComplete(google, map, mapOptions)

        const draggedPin = new google.maps.marker.PinElement({
            background: "#fee685",
            borderColor: "#e17100",
            glyphColor: "#e17100",
            scale: 1,
        });

        let pointOfInterestMarker = new google.maps.marker.AdvancedMarkerElement({
            map,
            gmpDraggable: true,
            content: draggedPin.element,
        });

        infoWindowObject.value = new google.maps.InfoWindow({});
        infoWindowObject.value.addListener('closeclick', () => {
            console.log('infoWindow closed')
            emit('closed-info-window')
        })
        let place;

        placeAutocomplete.value.addEventListener('gmp-select', async ({ placePrediction }) => {
            place = placePrediction.toPlace();
            await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });
            // If the place has a geometry, then present it on a map.
            if (place.viewport) {
                map.fitBounds(place.viewport);
            }
            else {
                map.setCenter(place.location);
                map.setZoom(newPinZoom.value);
            }
            let content = '<div id="infowindow-content">' +
                '<span id="place-displayname" class="title font-medium tracking-wider">' + place.displayName + '</span><br />' +
                '<span id="place-address">' + place.formattedAddress + '</span>' +
                '</div>';
            infoWindowObject.value.setContent(content);
            infoWindowObject.value.setPosition(place.location);
            infoWindowObject.value.open({
                map: map,
                anchor: pointOfInterestMarker,
                shouldFocus: false,
            });
            pointOfInterestMarker.position = place.location;
            pointOfInterestFilters.latitude = pointOfInterestMarker.position.lat
            pointOfInterestFilters.longitude = pointOfInterestMarker.position.lng
            addressLabelStyle.value = null
        })

        pointOfInterestMarker.addListener("dragend", (event) => {
            const position = pointOfInterestMarker.position;
            infoWindowObject.value.close();
            let manualPinContent = '<div id="infowindow-content">' +
                '<span id="place-displayname" class="title font-medium tracking-wider">Custom Location</span><br />' +
                `<span id="place-address">Pin dropped at lat: ${position.lat}, long: ${position.lng}</span>` +
                '</div>';
            infoWindowObject.value.setContent(manualPinContent);
            infoWindowObject.value.open(pointOfInterestMarker.map, pointOfInterestMarker);
            pointOfInterestFilters.latitude = position.lat
            pointOfInterestFilters.longitude = position.lng
            if(!addressLabelStyle.value) {
                addressLabelStyle.value = 'color: #99a1af;'
            }
        });

        map.addListener("zoom_changed", () => {
            resetMarkerClusters()
        });

        map.addListener("bounds_changed", () => {
            resetMarkerClusters()
        });
        
    }).catch((e) => {
        console.log(e)
    })
    emit('map-init-complete')
}

function initAutoComplete(google, map, mapOptions) {
    // let autocompleteInput = document.querySelector('[aria-label="Search For a Place"]')
    // autocompleteInput.value = ""
    // if(!placeAutocomplete.value) {
    placeAutocomplete.value = new google.maps.places.PlaceAutocompleteElement();
    placeAutocomplete.value.id = 'place-autocomplete-input';
    placeAutocomplete.value.locationBias = mapOptions.center;
    const card = document.getElementById('place-autocomplete-card');
    document.getElementById('place-autocomplete-container').appendChild(placeAutocomplete.value);
    map.controls[google.maps.ControlPosition.TOP_LEFT].push(card);
    // }

}

function buildMarkerContent(companyLocation) {
  const content = document.createElement("div");
  content.classList.add("companyLocation");
  content.innerHTML = `
    <div class="details">
        <div class="name font-medium tracking-wider pb-1">${companyLocation.company.company_name}</div>
        <div class="address">${companyLocation.location.address_line_1 ? companyLocation.location.address_line_1 : ''} ${companyLocation.location.address_line_2 ? companyLocation.location.address_line_2 : '' }</div>
        <div class="city state zip"> ${companyLocation.location.city ? companyLocation.location.city : ''}, ${companyLocation.location.state ? companyLocation.location.state : ''} ${companyLocation.location.zip ? companyLocation.location.zip : ''}</div>
        <div class="features pt-1">
            <div class="">ID: ${companyLocation.company.id}</div>
            <div>
                <i aria-hidden="true" class="fa fa-revene fa-lg revenue" title="revenue"></i>
                <span class="fa-sr-only">Revenue:</span>
                <span>${companyLocation.company.revenue}</span>
            </div>
            <div>
                <i aria-hidden="true" class="fa fa-headcount fa-lg headcount" title="headcount"></i>
                <span class="fa-sr-only">Headcount:</span>
                <span>${companyLocation.company.employee_headcount}</span>
            </div>
        </div>
    </div>
    `;
  return content;
}

function resetMarkers() {
    console.log('init markers')
    if(markers.value.length > 0) {
        markers.value.forEach((marker) => {
            marker.setMap(null)
        })
        // markerClusterer.value.clearMarkers()
        // if(markerClusterer.value) {
            
        // }
    }

    markers.value = props.companyLocations.map((companyLocation) => {
        const companyPin = new googleObject.value.maps.marker.PinElement({
            background: "#0F102E",
            borderColor: "#0F102E",
            glyphColor: "white",
            scale: 0.75,
        });
            const marker = new googleObject.value.maps.marker.AdvancedMarkerElement({
                position: {lat: parseFloat(companyLocation.location.lat), lng: parseFloat(companyLocation.location.long)},
                content: companyPin.element,
            });
            let city = companyLocation && companyLocation.location && companyLocation.location.city ? `${companyLocation.location.city}` : ''
            let state = companyLocation && companyLocation.location && companyLocation.location.state ? `, ${companyLocation.location.state}` : ''
            let country = companyLocation && companyLocation.location && companyLocation.location.country && companyLocation.location.country != 'USA' ? ` (${companyLocation.location.country})` : ''
            marker.setAttribute("city-state-country", `${city}${state}${country}`)
            marker.setAttribute("id", `marker-${companyLocation.id}`)
            marker.addListener('gmp-click', (event) => {
                handleLocationClick(marker, companyLocation);
            })
            return marker;
    });
    numLocations.value = markers.value.length
    markerClusterer.value.clearMarkers()
    markerClusterer.value.addMarkers(markers.value)
    // if(markerClusterer.value) {
  
    // }
    clusters.value = markerClusterer.value.clusters.sort((a, b) => b.markers.length - a.markers.length)
    if(clusters.value && clusters.value.length > 0) {
        emit('set-clusters', clusters.value)
    }
    return markers.value.length
}

function resetMarkerClusters() {
    clusters.value = markerClusterer.value.clusters.sort((a, b) => b.markers.length - a.markers.length)
    if(clusters.value && clusters.value.length > 0) {
        emit('set-clusters', clusters.value)
    }
}

function getMarkerClusterColor(markerCount, maxMarkerCount) {
    let markerColor
    if(markerCount < maxMarkerCount*0.25) {
        markerColor = '#8489C2E6'
        // markerColor = '#C7D2FD'
        // markerColor = `rgba(37,39,77,${markerCount / maxMarkerCount / 3 + 0.666})`
    } else if (markerCount < maxMarkerCount*0.5) {
        markerColor = '#3C3F7CE6'
    } else if (markerCount < maxMarkerCount*0.75) {
        markerColor = '#282B7AE6'
    } else {
        markerColor = '#24274DE6'
    }
    return markerColor
}

function toggleFilters() {
    visibleFilters.value = !visibleFilters.value
}

function handleLocationClick(markerView, companyLocation) {
    let content = buildMarkerContent(companyLocation)
    infoWindowObject.value.setContent(content);
    infoWindowObject.value.setPosition(markerView.location);
    infoWindowObject.value.open({
        map: mapObject.value,
        anchor: markerView,
        shouldFocus: false,
    });
    emit('select-location-pin', companyLocation)
}

function searchCompanyLocations() {
    emit('fetch-company-locations')
}

function reloadMap() {
    console.log('reloadMap', props.mapOptions.minZoom, props.mapOptions.maxZoom), 
    mapObject.value.setCenter(props.mapOptions.center)
    resetMarkers()
    setTimeout(() => {
        mapObject.value.setZoom(props.mapOptions.zoom)
        mapObject.value.setOptions({ maxZoom: props.mapOptions.maxZoom })
    }, numLocations.value.toString())
}

function centerMapAndZoom() {
    console.log('centerMapAndZoom', props.mapOptions.center, props.mapOptions.zoom, props.mapOptions.minZoom, props.mapOptions.maxZoom)
    mapObject.value.setOptions(props.mapOptions)
    mapObject.value.setCenter(props.mapOptions.center)
    mapObject.value.setZoom(props.mapOptions.zoom)
    mapObject.value.setOptions({ maxZoom: props.mapOptions.maxZoom })
}

function openCompanyLocationInfoWindow() {
    let content = buildMarkerContent(props.selectedCompanyLocation)
    infoWindowObject.value.setContent(content);
    infoWindowObject.value.setPosition({ lat: parseInt(props.selectedCompanyLocation.location.lat), lng: parseInt(props.selectedCompanyLocation.location.lng) });
    infoWindowObject.value.open({
        map: mapObject.value,
        anchor: markers.value.find((marker) => {
            return marker.getAttribute('id') == `marker-${props.selectedCompanyLocation.id}`
        }),
        shouldFocus: false,
    });
}


// async function loadMap(mapOptions=defaultMapOptions.value) {
//     mapLoader.importLibrary('maps').then(({Map}) => {
//         map.value = new Map(document.getElementById("map", mapOptions))
//     })
// }

// async function loadMarkerClusters() {
//     mapLoader.importLibrary('marker')
// }

</script>

<style>
.GMAMP-maps-pin-view { transition: all 0.15s linear; }
.GMAMP-maps-pin-view:hover { transform: scale(1.5); }
</style>